import {
  Avatar,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Input,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Box } from "@mui/system";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { BreadcrumbContext } from "../../../../components/Breadcrumb";
import {
  FirebaseAuth,
  FirebaseStorage,
  Firestore
} from "../../../../components/FirebaseAuth/firebase";
import { 
  serviceFormErrors, 
  serviceTypes,
  extensionCategories,
  extensionFrequencies,
  extensionPlatforms,
  extensionAccountTypes
} from "../../../../utils/constants";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { collection, orderBy, getDocs, doc, setDoc, query, addDoc } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    padding: 24,
    margin: "auto",
  },
  title: {
    fontSize: "1.3rem",
    fontWeight: 500,
  },
  imageContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  image: {
    width: 130,
    height: 130,
    marginBottom: 24,
  },
  uploadInput: {
    display: "none",
  },
  textField: {
    margin: "12px 0",
  },
  btnContainer: {
    display: "flex",
    flexDirection: "row",
    maxWidth: 160,
    justifyContent: "space-between",
    marginTop: 24,
  },
}));

const initialService = {
  name: "",
  description: "",
  type: "",
  provider: "",
  imageUrl: "",
  showOrganizations: [],
  excludeOrganizations: [],
  showAccounts: [],
  excludeAccounts: [],
  // Extension-specific fields
  category: "",
  frequency: "",
  platforms: [],
  accountTypes: [],
  stripePriceId: "",
  stripeProductId: "",
  functionName: "",
  configurationFields: [],
  learnMoreUrl: "",
  version: "1.0.0",
  active: true,
};

const AddService = () => {
  const history = useNavigate();
  const classes = useStyles();
  const { setBreadcrumb } = useContext(BreadcrumbContext);
  const [service, setService] = useState(initialService);
  const [organizations, setOrganizations] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [errors, setErrors] = useState();
  const [isExpert, setIsExpert] = useState(false);
  const [configFields, setConfigFields] = useState([]);

  const isExtensionType = service.type === "extension";

  // const getOrganizationData = useCallback(async () => {
  //   let temp = [];
  //   const organizationSnapShots = await FirebaseAuth.firestore()
  //     .collection("organizations")
  //     .orderBy("name", "asc")
  //     .get();
  //   organizationSnapShots.forEach((doc) => {
  //     temp.push({ ...doc.data(), id: doc.id });
  //   });
  //   setOrganizations(temp);
  // }, []);


  const getOrganizationData = useCallback(async () => {
    let temp = [];
    const organizationQuery = query(
      collection(Firestore, "organizations"),
      orderBy("name", "asc")
    );
    const organizationSnapShots = await getDocs(organizationQuery);
    organizationSnapShots.forEach((doc) => {
      temp.push({ ...doc.data(), id: doc.id });
    });
    setOrganizations(temp);
  }, []);

  // const getAccountsData = useCallback(async () => {
  //   let temp = [];
  //   const accountSnapShots = await FirebaseAuth.firestore()
  //     .collection("accounts")
  //     .orderBy("name", "asc")
  //     .get();
  //   accountSnapShots.forEach((doc) => {
  //     temp.push({ ...doc.data(), id: doc.id });
  //   });
  //   setAccounts(temp);
  // }, []);
  const getAccountsData = useCallback(async () => {
    let temp = [];
    const accountQuery = query(
      collection(Firestore, "accounts"),
      orderBy("name", "asc")
    );
  
    const accountSnapShots = await getDocs(accountQuery);
    accountSnapShots.forEach((doc) => {
      temp.push({ ...doc.data(), id: doc.id });
    });
    setAccounts(temp);
  }, []);

  const handleChange = useCallback(
    ({ target: { name, value } }) => {
      if (name === "name" && value.length > 99) return;
      if (name === "description" && value.length > 199) return;
      setService({ ...service, [name]: value });
    },
    [service]
  );

  // const handleUpload = async (image) => {
  //   try {
  //     const downloadUrl = await FirebaseStorage.ref(
  //       `services/${image.name}`
  //     ).put(image);
  //     return downloadUrl.ref.getDownloadURL();
  //   } catch (ex) {
  //     console.log("ex", ex);
  //   }
  // };

  const handleUpload = async (image) => {
    try {
      const storageRef = ref(FirebaseStorage, `services/${image.name}`);
      const uploadTask = uploadBytesResumable(storageRef, image);
      await uploadTask;
      const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
      return downloadUrl;
    } catch (ex) {
      console.log("ex", ex);
    }
  };

  const handleChangeImage = useCallback(async ({ target: { files } }) => {
    if (!!files[0]) {
      const uploadUrl = await handleUpload(files[0]);
      setService((prevService) => ({ ...prevService, imageUrl: uploadUrl }));
    }
  }, []);

  const handleBlur = useCallback(
    ({ target: { name, value } }) => {
      let isError = false;
      let message = "";
      if (value.trim().length < 1) {
        isError = true;
        message = serviceFormErrors[name]?.message;
      } else {
        isError = false;
        message = "";
      }
      setErrors({ ...errors, [name]: { isError: isError, message: message } });
    },
    [errors, setErrors]
  );

  const handleAdd = useCallback(async () => {
    if (!service.name) {
      setErrors({
        ...errors,
        name: { isError: true, message: serviceFormErrors.name.message },
      });
      return;
    }

    if (!service.description) {
      setErrors({
        ...errors,
        description: {
          isError: true,
          message: serviceFormErrors.description.message,
        },
      });
      return;
    }

    if (!service.provider) {
      setErrors({
        ...errors,
        provider: {
          isError: true,
          message: serviceFormErrors.provider.message,
        },
      });
    }

    if (!service.type) {
      setErrors({
        ...errors,
        type: { isError: true, message: serviceFormErrors.type.message },
      });
      return;
    }

    // Extension-specific validation
    if (service.type === "extension") {
      if (!service.category) {
        setErrors({
          ...errors,
          category: { isError: true, message: "Extension category is required." },
        });
        return;
      }
      
      if (!service.frequency) {
        setErrors({
          ...errors,
          frequency: { isError: true, message: "Execution frequency is required." },
        });
        return;
      }
      
      if (!service.functionName) {
        setErrors({
          ...errors,
          functionName: { isError: true, message: "Firebase function name is required." },
        });
        return;
      }
    }

    // try {
    //   const serviceRef = FirebaseAuth.firestore().collection("services").doc();
    //   await serviceRef.set({ ...service, isExpert });
    //   toast.success("Added Successfully");
    //   history.goBack();
    // } catch (error) {
    //   toast.error("Something went wrong");
    // }
    try {
      const servicesRef = collection(Firestore, "services");
      await addDoc(servicesRef, { ...service, isExpert });
      toast.success("Added Successfully");
      history(-1);
    } catch (error) {
      toast.error("Something went wrong");
    }
  }, [errors, service, history, isExpert]);

  const handleCancel = useCallback(() => {
    history(-1);
  }, [history]);

  useEffect(() => {
    getOrganizationData();
    getAccountsData();
  }, [getOrganizationData, getAccountsData]);

  useEffect(() => {
    setBreadcrumb([
      {
        to: "/",
        text: "Home",
        active: false,
      },
      {
        to: "/serviceLists",
        text: "Services",
        active: false,
      },
      {
        to: null,
        text: `Add Service`,
        active: true,
      },
    ]);
  }, [setBreadcrumb]);

  return (
    <Paper className={classes.container}>
      <Box className={classes.title}>Add Service</Box>
      <Grid container>
        <Grid item xs={12} md={3} className={classes.imageContainer}>
          <Avatar
            alt={service.imageUrl}
            src={service.imageUrl}
            className={classes.image}
          />
          <Stack>
            <label htmlFor="contained-button-file">
              <Input
                accept="image/*"
                id="contained-button-file"
                name="image"
                type="file"
                onChange={handleChangeImage}
                className={classes.uploadInput}
              />
              <IconButton
                color="primary"
                aria-label="upload picture"
                component="span"
              >
                <CloudUploadIcon />
                <Box>
                  <Typography ml={1}>Update Image</Typography>
                </Box>
              </IconButton>
            </label>
          </Stack>
        </Grid>
        <Grid item xs={12} md={9}>
          <Box>
            <TextField
              label="Service Name*"
              name="name"
              value={service?.name}
              fullWidth
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors?.name?.isError}
              helperText={errors?.name?.message}
              className={classes.textField}
            />
            <TextField
              label="Service Description*"
              name="description"
              value={service?.description}
              fullWidth
              multiline
              minRows={3}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors?.description?.isError}
              helperText={errors?.description?.message}
              className={classes.textField}
            />
            <TextField
              label="Service Provider*"
              name="provider"
              value={service?.provider}
              fullWidth
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors?.provider?.isError}
              helperText={errors?.provider?.message}
              className={classes.textField}
            />
            <FormControl fullWidth className={classes.textField}>
              <InputLabel id="type">Service Type*</InputLabel>
              <Select
                labelId="type"
                name="type"
                onChange={handleChange}
                value={service?.type}
                label="Service Type*"
              >
                {serviceTypes.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth className={classes.textField}>
              <InputLabel id="showOrganizations">Show Organizations</InputLabel>
              <Select
                labelId="showOrganizations"
                name="showOrganizations"
                onChange={handleChange}
                value={service.showOrganizations || []}
                label="Show Organizations"
                multiple
              >
                {organizations.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth className={classes.textField}>
              <InputLabel id="excludeOrganizations">
                Exclude Organizations
              </InputLabel>
              <Select
                labelId="excludeOrganizations"
                name="excludeOrganizations"
                onChange={handleChange}
                value={service.excludeOrganizations || []}
                label="Exclude Organizations"
                multiple
              >
                {organizations.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth className={classes.textField}>
              <InputLabel id="showAccounts">Show Accounts</InputLabel>
              <Select
                labelId="showAccounts"
                name="showAccounts"
                onChange={handleChange}
                value={service.showAccounts || []}
                label="Show Accounts"
                multiple
              >
                {accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth className={classes.textField}>
              <InputLabel id="excludeAccounts">Exclude Accounts</InputLabel>
              <Select
                labelId="excludeAccounts"
                name="excludeAccounts"
                onChange={handleChange}
                value={service.excludeAccounts || []}
                label="Exclude Accounts"
                multiple
              >
                {accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Service Url"
              name="link"
              value={service.link}
              fullWidth
              onChange={handleChange}
              required={false}
              className={classes.textField}
            />
            
            {/* Extension-specific fields */}
            {isExtensionType && (
              <>
                <FormControl fullWidth className={classes.textField}>
                  <InputLabel id="category">Extension Category*</InputLabel>
                  <Select
                    labelId="category"
                    name="category"
                    onChange={handleChange}
                    value={service.category}
                    label="Extension Category*"
                  >
                    {extensionCategories.map((category) => (
                      <MenuItem key={category.value} value={category.value}>
                        {category.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl fullWidth className={classes.textField}>
                  <InputLabel id="frequency">Execution Frequency*</InputLabel>
                  <Select
                    labelId="frequency"
                    name="frequency"
                    onChange={handleChange}
                    value={service.frequency}
                    label="Execution Frequency*"
                  >
                    {extensionFrequencies.map((freq) => (
                      <MenuItem key={freq.value} value={freq.value}>
                        {freq.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl fullWidth className={classes.textField}>
                  <InputLabel id="platforms">Supported Platforms</InputLabel>
                  <Select
                    labelId="platforms"
                    name="platforms"
                    onChange={handleChange}
                    value={service.platforms || []}
                    label="Supported Platforms"
                    multiple
                  >
                    {extensionPlatforms.map((platform) => (
                      <MenuItem key={platform.value} value={platform.value}>
                        {platform.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl fullWidth className={classes.textField}>
                  <InputLabel id="accountTypes">Required Collection Types</InputLabel>
                  <Select
                    labelId="accountTypes"
                    name="accountTypes"
                    onChange={handleChange}
                    value={service.accountTypes || []}
                    label="Required Collection Types"
                    multiple
                  >
                    {extensionAccountTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <TextField
                  label="Stripe Price ID"
                  name="stripePriceId"
                  value={service.stripePriceId}
                  fullWidth
                  onChange={handleChange}
                  className={classes.textField}
                  helperText="For paid extensions only"
                />
                
                <TextField
                  label="Stripe Product ID"
                  name="stripeProductId"
                  value={service.stripeProductId}
                  fullWidth
                  onChange={handleChange}
                  className={classes.textField}
                  helperText="For paid extensions only"
                />
                
                <TextField
                  label="Firebase Function Name*"
                  name="functionName"
                  value={service.functionName}
                  fullWidth
                  onChange={handleChange}
                  className={classes.textField}
                  helperText="Name of the Firebase function to execute"
                />
                
                <TextField
                  label="Learn More URL"
                  name="learnMoreUrl"
                  value={service.learnMoreUrl}
                  fullWidth
                  onChange={handleChange}
                  className={classes.textField}
                  helperText="Optional link to detailed documentation"
                />
                
                <TextField
                  label="Version"
                  name="version"
                  value={service.version}
                  fullWidth
                  onChange={handleChange}
                  className={classes.textField}
                />
              </>
            )}
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={isExpert}
                  onChange={(_, checked) => setIsExpert(checked)}
                  sx={{ padding: "0 4px" }}
                />
              }
              label="This is expert service."
              labelPlacement="end"
              sx={{ marginTop: "2px" }}
            />
          </Box>
          <Box className={classes.btnContainer}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAdd}
              className={classes.button}
            >
              Add
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleCancel}
              className={classes.button}
            >
              Cancel
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default AddService;
